// ============================================================================
// PRELIT-COMM-001 — Capture Commitment
// ============================================================================
// Medium modal. Action type defaults to CALL, disposition is locked to
// COMMITMENT_GIVEN. Promised date validated against
// config.LITIGATION_POLICY.prelit_commitment_window_days. Fires
// capture_commitment trigger (CONTACTING -> COMMITMENT) on save.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useCaptureCommitment } from 'hooks/api/usePreLitigation';
import type { ActionType } from 'api/types/preLitigation.types';

const ACTION_TYPE_OPTIONS: { value: ActionType; label: string }[] = [
  { value: 'CALL',         label: 'Phone Call' },
  { value: 'SMS',          label: 'SMS' },
  { value: 'EMAIL',        label: 'Email' },
  { value: 'FIELD_VISIT',  label: 'Field Visit' },
  { value: 'OFFICE_VISIT', label: 'Office Visit' },
];

interface FormState {
  actionType: ActionType;
  notes: string;
  committedAmount: string;       // string for input control
  committedDate: string;
}

interface CaptureCommitmentModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  /** Cap (in days) for committedDate. From config.LITIGATION_POLICY.prelit_commitment_window_days. */
  commitmentWindowDays?: number;
  onClose: () => void;
  onCaptured: () => void;
}

const buildInitial = (): FormState => ({
  actionType: 'CALL',
  notes: '',
  committedAmount: '',
  committedDate: '',
});

const CaptureCommitmentModal: React.FC<CaptureCommitmentModalProps> = ({
  isOpen, caseId, caseCode, commitmentWindowDays = 90, onClose, onCaptured,
}) => {
  const [form, setForm] = useState<FormState>(buildInitial());
  const snapRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const capture = useCaptureCommitment();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const next = buildInitial();
    setForm(next);
    snapRef.current = JSON.stringify(next);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => snapRef.current && JSON.stringify(form) !== snapRef.current, [form]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFieldErrors(p => { const n = { ...p }; delete n[field as string]; return n; });
    setForm(p => ({ ...p, [field]: value }));
  };

  const handleClose = useCallback(() => {
    if (capture.loading) return;
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, capture.loading, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const amt = parseFloat(form.committedAmount);
    if (!form.actionType) errors.actionType = 'Required';
    if (!form.committedAmount || Number.isNaN(amt) || amt <= 0) errors.committedAmount = 'Enter a positive amount.';
    if (!form.committedDate) errors.committedDate = 'Pick a promised date.';
    else {
      const d = new Date(form.committedDate);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const max = new Date(today); max.setDate(max.getDate() + commitmentWindowDays);
      if (d.getTime() < today.getTime()) errors.committedDate = 'Promised date must be today or later.';
      else if (d.getTime() > max.getTime()) errors.committedDate = `Promised date cannot exceed ${commitmentWindowDays} days from today.`;
    }
    if (form.notes.length > 2000) errors.notes = 'Max 2000 chars';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, commitmentWindowDays]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    try {
      await capture.run(caseId, {
        actionType: form.actionType,
        notes: form.notes || undefined,
        committedAmount: parseFloat(form.committedAmount),
        committedDate: form.committedDate,
      });
      notificationService.toast.success(`Commitment captured on ${caseCode}.`);
      onCaptured();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 409) {
        notificationService.toast.warning(msg ?? 'An open commitment already exists. Resolve it first.', 6000);
      } else {
        notificationService.toast.error(msg ?? 'Failed to capture commitment.', 6000);
      }
    }
  }, [capture, caseId, caseCode, form, onCaptured, onClose]);

  if (!isOpen) return null;

  const previewAmt = parseFloat(form.committedAmount);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {capture.loading && <LoadingOverlay text="Capturing commitment..." />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Capture Commitment — {caseCode}</h2>
          <button className="modal-close" onClick={handleClose} disabled={capture.loading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="actionType">
              <SyutSelect
                label={<>Action Type <span className="text-danger">*</span></>}
                options={ACTION_TYPE_OPTIONS}
                value={form.actionType}
                onChange={(v) => setField('actionType', v as ActionType)}
                isClearable={false}
                error={fieldErrors.actionType}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Disposition</label>
              <input type="text" className="form-input" value="COMMITMENT_GIVEN" disabled
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }} />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="committedAmount">
              <label className="form-label">Promised Amount ₹ <span className="text-danger">*</span></label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`form-input${fieldErrors.committedAmount ? ' form-field-error' : ''}`}
                value={form.committedAmount}
                onChange={(e) => setField('committedAmount', e.target.value)}
                placeholder="50000"
              />
              {!Number.isNaN(previewAmt) && previewAmt > 0 && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                  ₹{previewAmt.toLocaleString('en-IN')}
                </div>
              )}
              {fieldErrors.committedAmount && <div className="form-error-message">{fieldErrors.committedAmount}</div>}
            </div>
            <div className="form-group" data-field="committedDate">
              <label className="form-label">Promised Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${fieldErrors.committedDate ? ' form-field-error' : ''}`}
                value={form.committedDate}
                onChange={(e) => setField('committedDate', e.target.value)}
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                Up to {commitmentWindowDays} days from today.
              </div>
              {fieldErrors.committedDate && <div className="form-error-message">{fieldErrors.committedDate}</div>}
            </div>
          </div>

          <div className="form-group" data-field="notes">
            <label className="form-label">Notes <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
            <textarea
              className={`form-textarea${fieldErrors.notes ? ' form-field-error' : ''}`}
              rows={3}
              maxLength={2000}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
            />
            {fieldErrors.notes && <div className="form-error-message">{fieldErrors.notes}</div>}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={capture.loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={capture.loading}>Capture</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Commitment"
        message={`Capture promise of ₹${(previewAmt || 0).toLocaleString('en-IN')} due ${form.committedDate} on ${caseCode}?`}
        confirmLabel="Capture"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
  </div>
);

export default CaptureCommitmentModal;
